import { motion, useScroll, useTransform } from "framer-motion";
import { Star, MapPin, ChevronDown } from "lucide-react";
import { IMG } from "../data/content";
import { scrollToHash } from "./Navbar";

const LINES = ["Where Kashmir", "Welcomes You", "Home."];

const Hero = ({ onBook }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const scale = useTransform(scrollY, [0, 900], [1.08, 1.25]);
  const fade = useTransform(scrollY, [0, 450], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" className="relative flex h-[100svh] min-h-[680px] items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.lobby} alt="The Rayan grand lobby with carved Kashmiri walnut ceiling" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0B0A]/75 via-[#0D0B0A]/30 to-[#0D0B0A]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-36 sm:px-8 sm:pb-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-6 hidden flex-wrap items-center gap-4 sm:flex"
        >
          <span data-testid="hero-rating-badge" className="flex items-center gap-2 border border-[#C5A059]/40 bg-[#0D0B0A]/50 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[#C5A059] backdrop-blur-md">
            <Star size={12} fill="#C5A059" /> Rated by Guests
          </span>
          <span data-testid="hero-location-badge" className="flex items-center gap-2 border border-[#2A2421] bg-[#0D0B0A]/50 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[#A8A29E] backdrop-blur-md">
            <MapPin size={12} className="text-[#C5A059]" /> Hyderpora Bypass · Srinagar
          </span>
        </motion.div>

        <h1 className="font-display text-5xl font-light leading-[1.02] tracking-tight text-[#F5F2EB] sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-[#C5A059]" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-[#D6CDBE] sm:text-base"
        >
          Hand-carved walnut ceilings, backlit marble and unhurried Kashmiri hospitality — a boutique sanctuary
          ten minutes from Srinagar Airport and fifteen from Dal Lake.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-reserve-button"
            onClick={onBook}
            className="border border-[#C5A059] bg-[#C5A059] px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#0D0B0A] transition-all duration-300 hover:bg-transparent hover:text-[#C5A059]"
          >
            Reserve Your Stay
          </button>
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollToHash("#rooms")}
            className="border border-[#F5F2EB]/25 px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#F5F2EB] transition-all duration-300 hover:border-[#C5A059] hover:text-[#C5A059]"
          >
            Explore Rooms
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToHash("#booking")}
        style={{ opacity: fade }}
        className="absolute bottom-28 left-1/2 z-10 hidden -translate-x-1/2 text-[#C5A059] sm:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2 }}
        aria-label="Scroll to booking"
      >
        <ChevronDown size={26} />
      </motion.button>
    </section>
  );
};

export default Hero;
